// =====================================================
// Idle Timeout Hook
// AdminMunicipal v6.0 - Security Enhancement
// Automatically logs out users after inactivity period
// =====================================================

import { useState, useEffect, useCallback, useRef } from 'react';

interface IdleTimeoutConfig {
  /** Idle time before warning (ms) - default 25 minutes */
  warningTimeMs?: number;
  /** Time from warning to logout (ms) - default 5 minutes */
  logoutTimeMs?: number;
  /** Events to track as activity */
  events?: string[];
  /** Whether the timeout is enabled */
  enabled?: boolean;
}

interface IdleTimeoutReturn {
  /** Whether the warning modal should show */
  showWarning: boolean;
  /** Time remaining until logout (ms) */
  remainingTime: number;
  /** Reset the idle timer (call on user activity) */
  resetTimer: () => void;
  /** Dismiss warning and reset */
  dismissWarning: () => void;
}

const DEFAULT_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

/**
 * Hook to implement idle timeout for security.
 * Shows a warning before automatic logout.
 *
 * @param onLogout - Function to call when logout occurs
 * @param config - Configuration options
 * @returns State and actions for idle timeout
 *
 * @example
 * ```tsx
 * const { showWarning, remainingTime, dismissWarning } = useIdleTimeout(signOut, {
 *   warningTimeMs: 25 * 60 * 1000, // 25 minutes
 *   logoutTimeMs: 5 * 60 * 1000,   // 5 minutes after warning
 * });
 * ```
 */
export function useIdleTimeout(
  onLogout: () => void,
  config: IdleTimeoutConfig = {}
): IdleTimeoutReturn {
  const {
    warningTimeMs = 25 * 60 * 1000, // 25 minutes
    logoutTimeMs = 5 * 60 * 1000,   // 5 minutes after warning
    events = DEFAULT_EVENTS,
    enabled = true,
  } = config;

  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(logoutTimeMs);

  const warningTimerRef = useRef<number | null>(null);
  const logoutTimerRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);
  const isWarningShowing = useRef(false);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Start the logout countdown (called when warning appears)
  const startLogoutCountdown = useCallback(() => {
    isWarningShowing.current = true;
    setShowWarning(true);
    setRemainingTime(logoutTimeMs);

    // Countdown every second
    countdownRef.current = window.setInterval(() => {
      setRemainingTime((prev) => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          clearAllTimers();
          isWarningShowing.current = false;
          onLogout();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    // Backup logout timer (in case interval fails)
    logoutTimerRef.current = window.setTimeout(() => {
      clearAllTimers();
      isWarningShowing.current = false;
      onLogout();
    }, logoutTimeMs);
  }, [logoutTimeMs, onLogout, clearAllTimers]);

  // Reset the idle timer
  const resetTimer = useCallback(() => {
    if (!enabled) return;

    clearAllTimers();
    isWarningShowing.current = false;
    setShowWarning(false);
    setRemainingTime(logoutTimeMs);

    // Start the warning timer
    warningTimerRef.current = window.setTimeout(() => {
      startLogoutCountdown();
    }, warningTimeMs);
  }, [enabled, warningTimeMs, logoutTimeMs, clearAllTimers, startLogoutCountdown]);

  // Dismiss warning and reset (user clicked "Continue")
  const dismissWarning = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Handle activity events
  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      // Only reset if warning is NOT showing
      // Once warning shows, user must click "Continue"
      if (!isWarningShowing.current) {
        resetTimer();
      }
    };

    // Add throttling to prevent excessive resets
    let lastActivity = Date.now();
    const throttledHandler = () => {
      const now = Date.now();
      if (now - lastActivity > 1000) { // Throttle to max once per second
        lastActivity = now;
        handleActivity();
      }
    };

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, throttledHandler, { passive: true });
    });

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      clearAllTimers();
      events.forEach((event) => {
        window.removeEventListener(event, throttledHandler);
      });
    };
  }, [enabled, events, resetTimer, clearAllTimers]);

  return {
    showWarning,
    remainingTime,
    resetTimer,
    dismissWarning,
  };
}
